import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Filter, Plus, Database, Clock, Briefcase, User, Archive, Edit3, Eye, ChevronRight, BarChart3, Globe, Zap, UserCheck, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import scenarioService from "@/api/scenarioService";
import type { Scenario } from "@/api/scenarioService";
import ScenarioFormModal from "./ScenarioFormModal";
import { ScenarioAssignModal } from "./ScenarioAssignModal";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

// ─── Helpers ──────────────────────────────────────────────────────────────

const DIFFICULTY_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  EASY: { text: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  MEDIUM: { text: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  HARD: { text: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20" },
};

const MEETING_LABELS: Record<string, string> = {
  call: "Cold Call",
  demo: "Demo",
  negotiation: "Negotiation",
  objection: "Objection Handling",
};

const Shimmer = () => (
  <div className="rounded-3xl border border-white/[0.06] bg-[#1A1726]/50 p-6 animate-pulse space-y-4">
    <div className="flex justify-between items-start">
      <div className="h-5 w-24 rounded-full bg-white/[0.05]" />
      <div className="h-5 w-16 rounded-full bg-white/[0.05]" />
    </div>
    <div className="space-y-2">
      <div className="h-6 w-3/4 rounded-lg bg-white/[0.07]" />
      <div className="h-4 w-full rounded bg-white/[0.04]" />
    </div>
    <div className="flex gap-4 mt-4">
      <div className="h-4 w-20 rounded bg-white/[0.05]" />
      <div className="h-4 w-20 rounded bg-white/[0.05]" />
    </div>
    <div className="flex gap-2 pt-4 border-t border-white/[0.05]">
      <div className="h-9 flex-1 rounded-xl bg-white/[0.05]" />
      <div className="h-9 flex-1 rounded-xl bg-white/[0.05]" />
    </div>
  </div>
);

// ─── Scenario Card ────────────────────────────────────────────────────────

function ScenarioCard({
  scenario,
  onEdit,
  onArchive,
  onPreview,
  onAssign,
}: {
  scenario: Scenario;
  onEdit: (s: Scenario) => void;
  onArchive: (s: Scenario) => void;
  onPreview: (s: Scenario) => void;
  onAssign: (s: Scenario) => void;
}) {
  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="group relative rounded-[2rem] border border-white/[0.06] bg-[#1A1726] p-7 flex flex-col gap-5 hover:border-[#B8F890]/30 hover:bg-[#1E1B2E] transition-all duration-300 shadow-xl overflow-hidden"
    >
      {/* Background Accent */}
      <div className="absolute top-0 right-0 p-8 opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none">
        <Zap className="w-24 h-24 text-[#B8F890]" />
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 relative z-10">
        <div className="flex flex-wrap gap-2">
          {scenario.isSystemDefault && (
            <span className="text-[10px] bg-[#B8F890]/10 text-[#B8F890] border border-[#B8F890]/20 px-3 py-1 rounded-full font-bold uppercase tracking-widest">
              System
            </span>
          )}
          <span className={`text-[10px] px-3 py-1 rounded-full border font-bold uppercase tracking-widest ${DIFFICULTY_COLORS[scenario.difficulty]?.bg} ${DIFFICULTY_COLORS[scenario.difficulty]?.text} ${DIFFICULTY_COLORS[scenario.difficulty]?.border}`}>
            {scenario.difficulty}
          </span>
          <span className="text-[10px] bg-white/[0.05] text-[#fff] border border-white/[0.08] px-3 py-1 rounded-full font-bold uppercase tracking-widest">
            {MEETING_LABELS[scenario.meetingType] || scenario.meetingType}
          </span>
        </div>
      </div>

      <div className="relative z-10">
        <h3 className="text-white font-bold text-[18px] leading-tight group-hover:text-[#B8F890] transition-colors mb-2 line-clamp-2">{scenario.title}</h3>
        {scenario.description && (
          <p className="text-[16px] text-[#fff] line-clamp-2 leading-relaxed">{scenario.description}</p>
        )}
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-[14px] text-[#fff] relative z-10">
        <span className="flex items-center gap-1.5 hover:text-[#fff] transition-colors">
          <Briefcase className="w-3.5 h-3.5" />
          {scenario.industry}
        </span>
        <span className="flex items-center gap-1.5 hover:text-[#fff] transition-colors">
          <Clock className="w-3.5 h-3.5" />
          ~{scenario.avgDurationMin}m
        </span>
        {scenario.personaTitle && (
          <span className="flex items-center gap-1.5 hover:text-[#fff] transition-colors">
            <User className="w-3.5 h-3.5" />
            {scenario.personaTitle}
          </span>
        )}
      </div>

      {/* Objection count badge */}
      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#fff] relative z-10">
        {scenario.keyObjections.length} VARIANT{scenario.keyObjections.length !== 1 ? "S" : ""}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-4 mt-auto border-t border-white/[0.05] relative z-10">
        <button
          onClick={() => onPreview(scenario)}
          className="flex-1 flex items-center justify-center gap-2 text-[14px] font-bold text-[#fff] hover:text-white bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] rounded-xl py-2.5 transition-all"
        >
          <Eye className="w-3.5 h-3.5" />
          PREVIEW
        </button>
        <button
          onClick={() => onAssign(scenario)}
          className="flex items-center justify-center w-10 text-blue-400 bg-blue-500/5 border border-blue-500/20 hover:bg-blue-500/15 rounded-xl py-2.5 transition-all"
          title="Assign"
        >
          <UserCheck className="w-3.5 h-3.5" />
        </button>
        {!scenario.isSystemDefault && (
          <>
            <button
              onClick={() => onEdit(scenario)}
              className="flex items-center justify-center w-10 text-[#B8F890] bg-[#B8F890]/5 border border-[#B8F890]/20 hover:bg-[#B8F890]/15 rounded-xl py-2.5 transition-all"
              title="Edit"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onArchive(scenario)}
              className="flex items-center justify-center w-10 text-rose-400 bg-rose-500/5 border border-rose-500/20 hover:bg-rose-500/15 rounded-xl py-2.5 transition-all"
              title="Archive"
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────

function PreviewModal({ scenario, onClose }: { scenario: Scenario; onClose: () => void }) {
  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-md" 
        onClick={onClose}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[#0f0f0f] border border-white/[0.08] rounded-[2.5rem] w-full max-w-xl p-10 space-y-8 shadow-2xl relative overflow-y-auto overflow-x-hidden max-h-[90vh]" 
          onClick={e => e.stopPropagation()}
        >
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
             <Globe className="w-32 h-32 text-[#B8F890]" />
          </div>

          <div className="flex items-start justify-between relative z-10">
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <span className={`text-[10px] px-3 py-1 rounded-full border font-bold uppercase tracking-widest ${DIFFICULTY_COLORS[scenario.difficulty]?.bg} ${DIFFICULTY_COLORS[scenario.difficulty]?.text} ${DIFFICULTY_COLORS[scenario.difficulty]?.border}`}>{scenario.difficulty}</span>
                <span className="text-[10px] bg-white/[0.05] text-[#fff] border border-white/[0.08] px-3 py-1 rounded-full font-bold uppercase tracking-widest">{scenario.industry}</span>
              </div>
              <h2 className="text-white font-bold text-2xl leading-tight">{scenario.title}</h2>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-[#fff] hover:text-white transition-all shrink-0">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6 relative z-10">
            {scenario.description && (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#fff] font-black">Scenario Backdrop</p>
                <p className="text-[#fff] text-[16px] leading-relaxed">{scenario.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-widest text-[#fff] mb-1">Buyer Persona</p>
                <p className="text-white text-[16px] font-bold">{scenario.personaTitle || "Decision Maker"}</p>
                {scenario.personaStyle && <p className="text-[#fff] text-[14px] mt-1">{scenario.personaStyle}</p>}
              </div>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-widest text-[#fff] mb-1">Meeting Format</p>
                <p className="text-white text-[16px] font-bold capitalize">{MEETING_LABELS[scenario.meetingType] || scenario.meetingType}</p>
                <p className="text-[#fff] text-[14px] mt-1">Target: ~{scenario.avgDurationMin} minutes</p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#fff] font-black">Objection Variants ({scenario.keyObjections.length})</p>
              <div className="grid gap-2">
                {scenario.keyObjections.map((obj, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white/[0.02] border border-white/[0.05] rounded-2xl px-4 py-3 hover:bg-white/[0.04] transition-colors">
                    <span className="text-[#B8F890] text-[10px] font-black shrink-0 mt-1">#{i + 1}</span>
                    <p className="text-[#fff] text-[16px] italic">"{obj}"</p>
                  </div>
                ))}
              </div>
            </div>

            {scenario.scriptHints && (
              <div className="bg-[#B8F890]/5 border border-[#B8F890]/15 rounded-[1.5rem] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-3.5 h-3.5 text-[#B8F890]" />
                  <p className="text-[10px] uppercase tracking-widest text-[#B8F890] font-black">Coach Insights</p>
                </div>
                <p className="text-[#fff] text-[16px] leading-relaxed">{scenario.scriptHints}</p>
              </div>
            )}
          </div>
          
          <div className="pt-4">
             <button 
              onClick={onClose}
              className="w-full bg-white/5 hover:bg-white/10 text-[#fff] hover:text-white font-bold py-3.5 rounded-2xl transition-all uppercase tracking-widest text-[14px]"
             >
               Close Preview
             </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────

const INDUSTRIES = [
  "All Industries",
  "Technology / SaaS",
  "Financial Services",
  "Healthcare",
  "Real Estate / Mortgage",
  "Consumer Sales",
  "Home Services",
  "Automotive",
  "Insurance",
  "Professional Services",
  "Manufacturing / Industrial",
  "Education / Training",
  "Other",
];

export default function ScenarioLibrary() {
  const { isDark } = useDashboardTheme();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [industry, setIndustry] = useState("");
  const [search, setSearch] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [sortDate] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);

  const [showCreate, setShowCreate] = useState(false);
  const [editScenario, setEditScenario] = useState<any | null>(null);
  const [previewScenario, setPreviewScenario] = useState<Scenario | null>(null);
  const [assignScenario, setAssignScenario] = useState<Scenario | null>(null);
  const [incomingReturnTo, setIncomingReturnTo] = useState<string | null>(null);
  const [repContext, setRepContext] = useState<{ id: string, name: string } | null>(null);

  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      const stateObj = location.state as any;
      if (stateObj.showCreate) {
        if (stateObj.prefilledFields) {
          setEditScenario(stateObj.prefilledFields);
        } else {
          setShowCreate(true);
        }
        // capture returnTo so we can navigate back after creation
        setIncomingReturnTo(stateObj.returnTo || null);
        if (stateObj.repContext) setRepContext(stateObj.repContext);
      }
      if (stateObj.search) {
        setSearch(stateObj.search);
      }
      // Clear window history state so refresh doesn't pop it up again
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await scenarioService.getScenarios({
        industry: industry || undefined,
        search: search || undefined,
        difficulty: difficulty || undefined,
        dateFilter: dateFilter || undefined,
        sortDate,
        page,
        limit: 12,
      });
      setScenarios(res.scenarios);
      setTotal(res.total);
    } catch {
      setError("Failed to load scenarios");
    } finally {
      setLoading(false);
    }
  }, [industry, search, difficulty, dateFilter, sortDate, page]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = (s: Scenario) => {
    toast(
      ({ closeToast }) => (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium text-white">Archive "{s.title}"? Reps will no longer see it.</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={closeToast as any}
              className="px-3 py-1.5 text-xs font-bold text-white/70 hover:text-white transition-colors"
            >
              CANCEL
            </button>
            <button
              onClick={async () => {
                (closeToast as any)();
                try {
                  await scenarioService.archiveScenario(s.id);
                  toast.success("Scenario archived successfully");
                  load();
                } catch {
                  toast.error("Failed to archive scenario");
                }
              }}
              className="px-3 py-1.5 text-xs font-bold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 rounded-lg transition-colors"
            >
              ARCHIVE
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      }
    );
  };



  const totalPages = Math.max(1, Math.ceil(total / 12));

  return (
    <main className={"flex-1 p-6 md:p-10 mt-15 min-h-screen " + (isDark ? "bg-[#0a0a0a]" : "light-theme-dashboard")} style={{ background: isDark ? '#0a0a0a' : '#F4F5F7' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-10 flex-wrap gap-6">
        <div>
          <h1 className="text-white font-black text-4xl tracking-tight">
            Practice <span className="text-[#B8F890]">Engine</span>
          </h1>
          <p className="text-[#fff] text-[16px] mt-2 font-medium">Configure and manage scenario library for AI-powered sales roleplay</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 text-[14px] bg-[#B8F890] text-black font-black px-6 py-3 rounded-2xl hover:bg-[#a0e070] transition-all shadow-[0_0_20px_rgba(184,248,144,0.2)] active:scale-95"
          >
            <Plus className="w-4 h-4" />
            CREATE SCENARIO
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Total Library", value: total, icon: Globe, color: "text-blue-400" },
          { label: "System Core", value: scenarios.filter(s => s.isSystemDefault).length, icon: Database, color: "text-purple-400" },
          { label: "Custom Content", value: scenarios.filter(s => !s.isSystemDefault).length, icon: Edit3, color: "text-amber-400" },
          { label: "Active Verticals", value: new Set(scenarios.map(s => s.industry)).size, icon: BarChart3, color: "text-[#B8F890]" },
        ].map(stat => (
          <div key={stat.label} className="bg-[#1A1726]/80 border border-white/[0.06] rounded-3xl p-6 flex flex-col gap-1 shadow-lg relative overflow-hidden group">
            <stat.icon className={`absolute -right-4 -bottom-4 w-24 h-24 ${stat.color} opacity-[0.03] group-hover:opacity-[0.08] transition-opacity`} />
            <p className="text-[10px] uppercase tracking-widest text-[#fff] font-bold">{stat.label}</p>
            <p className="text-3xl font-black text-white">{loading ? "—" : stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-[#1A1726]/40 backdrop-blur-sm border border-white/[0.06] rounded-[2rem] p-5 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative lg:col-span-2 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#fff] group-focus-within:text-[#B8F890] transition-colors" size={16} />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by title, industry, or persona…"
              className="w-full rounded-2xl border border-white/[0.08] bg-[#1E1B2E]/60 text-[16px] text-white placeholder-gray-700 pl-11 pr-4 py-3.5 outline-none focus:border-[#B8F890]/30 transition-all shadow-inner"
            />
          </div>
          <div className="relative group">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-[#fff] group-focus-within:text-[#B8F890] transition-colors" size={16} />
            <select
              value={industry}
              onChange={e => { setIndustry(e.target.value === "All Industries" ? "" : e.target.value); setPage(1); }}
              className="w-full rounded-2xl border border-white/[0.08] bg-[#1E1B2E]/60 text-[16px] text-[#fff] pl-11 pr-10 py-3.5 outline-none focus:border-[#B8F890]/30 transition-all appearance-none cursor-pointer"
            >
              {INDUSTRIES.map(ind => <option key={ind} value={ind === "All Industries" ? "" : ind} className="bg-[#0f0f0f]">{ind}</option>)}
            </select>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#fff] rotate-90 pointer-events-none" />
          </div>
          <div className="relative group">
            <BarChart3 className="absolute left-4 top-1/2 -translate-y-1/2 text-[#fff] group-focus-within:text-[#B8F890] transition-colors" size={16} />
            <select
              value={difficulty}
              onChange={e => { setDifficulty(e.target.value); setPage(1); }}
              className="w-full rounded-2xl border border-white/[0.08] bg-[#1E1B2E]/60 text-[16px] text-[#fff] pl-11 pr-10 py-3.5 outline-none focus:border-[#B8F890]/30 transition-all appearance-none cursor-pointer"
            >
              <option value="" className="bg-[#0f0f0f]">All Difficulties</option>
              <option value="EASY" className="bg-[#0f0f0f]">Easy</option>
              <option value="MEDIUM" className="bg-[#0f0f0f]">Medium</option>
              <option value="HARD" className="bg-[#0f0f0f]">Hard</option>
            </select>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#fff] rotate-90 pointer-events-none" />
          </div>
          <div className="relative group">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-[#fff] group-focus-within:text-[#B8F890] transition-colors" size={16} />
            <select
              value={dateFilter}
              onChange={e => { setDateFilter(e.target.value); setPage(1); }}
              className="w-full rounded-2xl border border-white/[0.08] bg-[#1E1B2E]/60 text-[16px] text-[#fff] pl-11 pr-10 py-3.5 outline-none focus:border-[#B8F890]/30 transition-all appearance-none cursor-pointer"
            >
              <option value="" className="bg-[#0f0f0f]">All Time</option>
              <option value="7d" className="bg-[#0f0f0f]">Last 7 Days</option>
              <option value="30d" className="bg-[#0f0f0f]">Last 30 Days</option>
              <option value="90d" className="bg-[#0f0f0f]">Last 90 Days</option>
            </select>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#fff] rotate-90 pointer-events-none" />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-8 px-6 py-4 rounded-3xl bg-rose-500/5 border border-rose-500/20 text-rose-400 text-[16px] font-bold flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0" />
          {error}
        </div>
      )}

      {/* Grid Header */}
      <div className="flex items-center justify-between mb-6 px-2">
        <p className="text-xs font-bold uppercase tracking-widest text-[#fff]">
          {loading ? "Discovering..." : <><span className="text-[#fff]">{total}</span> Results found</>}
        </p>
        {!loading && totalPages > 1 && <p className="text-xs font-bold uppercase tracking-widest text-[#fff]">Page {page} of {totalPages}</p>}
      </div>

      {/* Grid */}
      <AnimatePresence mode="popLayout">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <Shimmer key={i} />)
            : scenarios.length === 0
              ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="col-span-full flex flex-col items-center justify-center py-32 rounded-[2.5rem] border border-dashed border-white/[0.1] bg-[#1A1726]/20 gap-6 text-center"
                >
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center text-[#fff]">
                    <Search size={40} />
                  </div>
                  <div>
                    <p className="text-white text-xl font-bold">No results found</p>
                    <p className="text-[#fff] mt-1 max-w-xs mx-auto">Try broadening your search or creating a new scenario.</p>
                  </div>
                  <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 text-[14px] font-black uppercase tracking-widest text-[#B8F890] border border-[#B8F890]/30 rounded-2xl px-8 py-3 hover:bg-[#B8F890]/10 transition-all">
                    Create New Scenario
                  </button>
                </motion.div>
              )
              : scenarios.map((s) => (
                <ScenarioCard
                  key={s.id}
                  scenario={s}
                  onEdit={setEditScenario}
                  onArchive={handleArchive}
                  onPreview={setPreviewScenario}
                  onAssign={setAssignScenario}
                />
              ))
          }
        </div>
      </AnimatePresence>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-12">
          <button 
            disabled={page === 1} 
            onClick={() => setPage(p => p - 1)} 
            className="w-12 h-12 rounded-2xl border border-white/[0.08] bg-[#1A1726]/40 text-[#fff] hover:text-[#B8F890] hover:border-[#B8F890]/30 disabled:opacity-20 disabled:cursor-not-allowed transition-all flex items-center justify-center active:scale-90 shadow-lg"
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
          
          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button 
                key={p} 
                onClick={() => setPage(p)} 
                className={`w-12 h-12 rounded-2xl text-xs font-black transition-all shadow-lg ${page === p ? "bg-[#B8F890] text-black scale-110" : "bg-[#1A1726]/40 border border-white/[0.08] text-[#fff] hover:text-white hover:bg-[#1E1B2E]"}`}
              >
                {p}
              </button>
            ))}
          </div>

          <button 
            disabled={page === totalPages} 
            onClick={() => setPage(p => p + 1)} 
            className="w-12 h-12 rounded-2xl border border-white/[0.08] bg-[#1A1726]/40 text-[#fff] hover:text-[#B8F890] hover:border-[#B8F890]/30 disabled:opacity-20 disabled:cursor-not-allowed transition-all flex items-center justify-center active:scale-90 shadow-lg"
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      )}

      {/* Modals */}
      {previewScenario && <PreviewModal scenario={previewScenario} onClose={() => setPreviewScenario(null)} />}
      
      {assignScenario && (
        <ScenarioAssignModal 
          open={true}
          onClose={() => setAssignScenario(null)}
          scenarioId={assignScenario.id}
          scenarioTitle={assignScenario.title}
          onSuccess={() => toast.success("Assigned successfully!")}
        />
      )}

      <AnimatePresence>
        {(showCreate || editScenario) && (
            <ScenarioFormModal
              existing={editScenario}
              repContext={repContext}
              onClose={() => { setShowCreate(false); setEditScenario(null); setRepContext(null); }}
              onSaved={(saved: any) => {
                setShowCreate(false); setEditScenario(null); setRepContext(null); load();
                if (incomingReturnTo && saved && saved.id) {
                  navigate(incomingReturnTo, { state: { createdScenarioId: saved.id } });
                }
              }}
            />
        )}
      </AnimatePresence>
    </main>
  );
}
